import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Link,
  Typography,
} from '@mui/material';
import { WarningOutlined } from '@mui/icons-material';
import {
  BRANCH_BLOCKED_EVENT,
  BRANCH_STATUS_REFRESHED_EVENT,
  getCurrentBranch,
  getWarningMessages,
  hasBranchWarning,
  isBranchBlocked,
} from 'src/utils/branchStatus';

const PAYMENT_WARNING_MESSAGE =
  'Ödenmemiş faturanız bulunmaktadır. 5 iş günü içinde ödemenizi tamamlamazsanız hesabınız blokaja alınacaktır.';

const SUPPORT_PHONE = '0850 532 11 11';

export default function BranchWarningBanner() {
  const [status, setStatus] = useState(null); // 'blocked' | 'warning' | null
  const [branchID, setBranchID] = useState(null);
  const [warningMessages, setWarningMessages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const evaluateBranchStatus = () => {
      const branch = getCurrentBranch();
      if (!branch) return;

      const blocked = isBranchBlocked(branch);
      const warned = hasBranchWarning(branch);
      const currentStatus = blocked ? 'blocked' : warned ? 'warning' : null;

      setStatus(currentStatus);
      setBranchID(branch.branchID);
      setWarningMessages(getWarningMessages(branch));

      if (currentStatus) {
        const dismissedKey = `${currentStatus}Dismissed_${branch.branchID}`;
        if (!sessionStorage.getItem(dismissedKey)) {
          setDialogOpen(true);
        }
      }
    };

    evaluateBranchStatus();

    window.addEventListener(
      BRANCH_STATUS_REFRESHED_EVENT,
      evaluateBranchStatus
    );
    return () =>
      window.removeEventListener(
        BRANCH_STATUS_REFRESHED_EVENT,
        evaluateBranchStatus
      );
  }, []);

  useEffect(() => {
    const handleBlockedDialogRequest = () => {
      const branch = getCurrentBranch();
      if (branch && isBranchBlocked(branch)) {
        setDialogOpen(true);
      }
    };

    window.addEventListener(BRANCH_BLOCKED_EVENT, handleBlockedDialogRequest);
    return () =>
      window.removeEventListener(
        BRANCH_BLOCKED_EVENT,
        handleBlockedDialogRequest
      );
  }, []);

  if (!status) return null;

  const handleClose = () => {
    sessionStorage.setItem(`${status}Dismissed_${branchID}`, '1');
    setDialogOpen(false);
  };

  if (status === 'blocked') {
    return (
      <>
        <Alert
          severity="error"
          icon={<WarningOutlined fontSize="inherit" />}
          sx={{ borderRadius: 0, mb: 2 }}
        >
          Mağazanızın taşıma işlemleri askıya alınmıştır. Cari'nize kesilen
          faturayı ödemek için{' '}
          <Link
            href="/pay-bill"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            panel.malllogistics.com/pay-bill
          </Link>{' '}
          adresinden yapabilirsiniz.
          <br />
          <br />
          Destek numarası: <strong>{SUPPORT_PHONE}</strong>
        </Alert>

        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <WarningOutlined sx={{ color: 'error.main', fontSize: 32 }} />
            </Box>

            <Typography variant="h6" sx={{ mb: 1 }}>
              Taşıma İşlemi Askıya Alındı
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Mağazanızın taşıma işlemleri askıya alınmıştır. Cari'nize kesilen
              faturayı ödemek için{' '}
              <Link
                href="/pay-bill"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                panel.malllogistics.com/pay-bill
              </Link>{' '}
              adresinden yapabilirsiniz.
              <br />
              <br />
              Destek numarası: <strong>{SUPPORT_PHONE}</strong>
            </Typography>

            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={handleClose}
            >
              Tamam
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Alert severity="warning" sx={{ borderRadius: 0, mb: 2 }}>
        {PAYMENT_WARNING_MESSAGE}
        {warningMessages.length > 0 && ' ' + warningMessages.join(' ')}
      </Alert>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'warning.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <WarningOutlined sx={{ color: 'warning.dark', fontSize: 32 }} />
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Ödeme Hatırlatması
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Cari'nize ait ödenmemiş faturalar bulunmaktadır. 5 iş günü içinde
            ödemenizi rica ederiz.
            {warningMessages.length > 0 && (
              <>
                <br />
                <br />
                {warningMessages.join(' ')}
              </>
            )}
          </Typography>

          <Button
            variant="contained"
            color="warning"
            fullWidth
            onClick={handleClose}
          >
            Anladım
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
